import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Icon } from "@/components/Icon";
import { toast } from "sonner";

import api, { apiError } from "@/lib/api";
import CasinoTile from "@/components/casino/CasinoTile";
import CasinoShelf from "@/components/casino/CasinoShelf";
import { isLiveDealer } from "@/lib/casinoClassification";

// Pretty provider label for shelves + badges.
const PROVIDER_LABEL = { jacktop: "JackTop", kerma: "Kerma", mock: "Sandbox" };


// Pretty label for the launched wallet so the player can SEE which currency this
// provider game is transacting in (UIB-11a). External providers never use the bonus
// wallet, so only coins/cash are possible here.
const WALLET_LABEL = { coins: "PCoins", cash: "PCash" };

export function ProviderGameHost({ launch, onClose }) {
  const formRef = useRef(null);
  const hostRef = useRef(null);
  const closeBtnRef = useRef(null);
  const frameName = useMemo(
    () => `pnr-provider-frame-${Math.random().toString(36).slice(2)}`,
    []
  );
  // Iframe load state so we never show a blank black frame as if the game loaded.
  // A cross-origin load firing onLoad is NOT proof the provider transacted — it only
  // means the document responded — but a load timeout / error IS a real failure we must
  // surface with a bounded retry/exit (UIB-11c).
  const [frameState, setFrameState] = useState("loading"); // loading | loaded | error
  const [reloadKey, setReloadKey] = useState(0);
  const timeoutRef = useRef(null);

  useEffect(() => {
    if (launch?.method === "POST" && formRef.current) {
      formRef.current.submit();
    }
  }, [launch, reloadKey]);

  // Reset load state whenever a new game opens or the player retries.
  useEffect(() => {
    if (!launch) return undefined;
    setFrameState("loading");
    clearTimeout(timeoutRef.current);
    // If nothing loads within 20s, treat it as a load failure (bounded, not forever spin).
    timeoutRef.current = setTimeout(() => {
      setFrameState((s) => (s === "loaded" ? s : "error"));
    }, 20000);
    return () => clearTimeout(timeoutRef.current);
  }, [launch, reloadKey]);

  // Lock the page behind the full-screen game so there is no double scroll / background
  // scroll bleed while a provider game is open (restored on close).
  useEffect(() => {
    if (!launch) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [launch]);

  // Dialog a11y (UIB-07): Escape closes the game, focus moves into the host on open and returns
  // to the trigger on close, and Tab is trapped within the host while it's open.
  useEffect(() => {
    if (!launch) return undefined;
    const host = hostRef.current;
    const prevFocus = document.activeElement;
    const focusables = () => Array.from(
      host?.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])') || []
    ).filter((el) => !el.disabled && el.offsetParent !== null);
    (closeBtnRef.current || focusables()[0] || host)?.focus?.();
    const onKey = (e) => {
      if (e.key === "Escape") { e.preventDefault(); onClose && onClose(); return; }
      if (e.key === "Tab") {
        const f = focusables();
        if (!f.length) return;
        const firstEl = f[0], lastEl = f[f.length - 1];
        if (e.shiftKey && document.activeElement === firstEl) { e.preventDefault(); lastEl.focus(); }
        else if (!e.shiftKey && document.activeElement === lastEl) { e.preventDefault(); firstEl.focus(); }
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      prevFocus && prevFocus.focus && prevFocus.focus();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [launch]);

  if (!launch) return null;
  const method = (launch.method || "GET").toUpperCase();
  const payload = launch.launch_payload || {};
  const walletLabel = WALLET_LABEL[launch.wallet] || null;

  const onFrameLoad = () => {
    clearTimeout(timeoutRef.current);
    setFrameState("loaded");
  };
  const onFrameError = () => {
    clearTimeout(timeoutRef.current);
    setFrameState("error");
  };
  const retry = () => { setReloadKey((k) => k + 1); };

  // Render through a portal to <body> so the full-screen overlay is always viewport-relative
  // — immune to any parent layout (e.g. the lobby's `space-y-8` margin that otherwise shoved
  // the fixed shell down 32px and overflowed the bottom), transforms, or overflow clipping.
  return createPortal(
    /* Full-viewport game shell. Uses 100dvh (dynamic viewport) so mobile browser chrome
       never crops it, and sits at z-[100] ABOVE the app's own bottom nav (z-30) so the nav
       is fully covered — no reserved footer strip stealing height. The `game-host-*`
       classes (index.css) collapse the header to a floating exit control in mobile
       LANDSCAPE (orientation + short height) so the game gets the whole screen, while
       keeping a normal compact header in portrait and on desktop. Orientation changes
       re-evaluate the media queries live, so the iframe resizes without a reload. */
    <div ref={hostRef} role="dialog" aria-modal="true" aria-label={launch.gameName || "Game"} tabIndex={-1} className="game-host fixed inset-0 z-[100] flex flex-col bg-black outline-none" data-testid="provider-game-host">
      <div className="game-host__chrome flex items-center justify-between border-b border-white/10 bg-[#0A0A0A] px-4 py-2.5">
        <div className="min-w-0">
          <p className="truncate text-[11px] font-bold uppercase tracking-[0.18em] text-[#a1a1aa]">
            {PROVIDER_LABEL[launch.provider] || launch.provider}
          </p>
          <div className="flex items-center gap-2">
            <p className="truncate font-display text-base font-black text-white">{launch.gameName}</p>
            {walletLabel && (
              <span className="inline-flex shrink-0 items-center gap-1 rounded-full border border-[#27272A] bg-[#141417] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#a1a1aa]">
                <Icon name={launch.wallet === "cash" ? "pnrcash" : "pnrcoin"} size={12} className={launch.wallet === "cash" ? "text-white" : "text-[#00D632]"} /> {walletLabel}
              </span>
            )}
          </div>
        </div>
        <button
          ref={closeBtnRef}
          type="button"
          onClick={onClose}
          aria-label="Exit game"
          className="ml-3 inline-flex shrink-0 items-center gap-2 rounded-full border border-[#27272A] px-4 py-2 text-sm font-bold text-white hover:border-[#00D632]"
        >
          <Icon name="x" size={16} /> Exit game
        </button>
      </div>

      {/* Floating exit — only shown in mobile landscape (header is hidden there). Respects
          the notch via safe-area insets so it never sits under the camera cutout. */}
      <button
        type="button"
        onClick={onClose}
        data-testid="provider-game-exit-floating"
        className="game-host__exit-floating hidden items-center gap-1.5 rounded-full border border-white/20 bg-black/70 px-3 py-1.5 text-xs font-bold text-white backdrop-blur-sm hover:border-[#00D632]"
        aria-label="Exit game"
      >
        <Icon name="x" size={14} /> Exit
      </button>

      {method === "POST" && (
        <form
          ref={formRef}
          action={launch.launch_url}
          method="post"
          target={frameName}
          className="hidden"
        >
          {Object.entries(payload).map(([key, value]) => (
            <input key={key} type="hidden" name={key} value={String(value ?? "")} />
          ))}
        </form>
      )}

      <div className="relative min-h-0 w-full flex-1">
        <iframe
          key={reloadKey}
          title={launch.gameName}
          name={frameName}
          src={method === "GET" ? launch.launch_url : "about:blank"}
          onLoad={onFrameLoad}
          onError={onFrameError}
          className="game-host__frame absolute inset-0 h-full w-full border-0 bg-black"
          allow="autoplay; fullscreen; encrypted-media"
          allowFullScreen
        />
        {/* Loading spinner until the provider document responds. */}
        {frameState === "loading" && (
          <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center gap-3 bg-black" data-testid="provider-game-loading">
            <span className="h-9 w-9 animate-spin rounded-full border-2 border-white/15 border-t-[#00D632]" />
            <p className="text-sm font-semibold text-[#a1a1aa]">Loading {launch.gameName}…</p>
          </div>
        )}
        {/* Bounded failure state — retry re-mounts the frame, exit closes the host. */}
        {frameState === "error" && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 bg-black px-6 text-center" data-testid="provider-game-error">
            <Icon name="alert-circle" size={36} className="text-[#EF4444]" />
            <div>
              <p className="font-display text-lg font-black text-white">Couldn&apos;t load this game</p>
              <p className="mt-1 text-sm text-[#a1a1aa]">The provider didn&apos;t respond in time. Your balance was not charged by us for this launch.</p>
            </div>
            <div className="flex items-center gap-3">
              <button type="button" onClick={retry} data-testid="provider-game-retry"
                className="inline-flex items-center gap-2 rounded-full bg-[#00D632] px-5 py-2.5 text-sm font-bold text-[#0A0A0A] hover:bg-[#00B829]">
                <Icon name="refresh" size={15} /> Retry
              </button>
              <button type="button" onClick={onClose} data-testid="provider-game-error-exit"
                className="inline-flex items-center gap-2 rounded-full border border-[#27272A] px-5 py-2.5 text-sm font-bold text-white hover:border-[#00D632]">
                <Icon name="x" size={15} /> Exit
              </button>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}


export default function ProviderCasinoShelf({ currency, query = "", catFilter = "lobby", refresh, onEmptyChange, variant = "default" }) {
  const isFeatured = variant === "featured";
  const [games, setGames] = useState([]);
  const [loadFailed, setLoadFailed] = useState(false);
  const [loadingGame, setLoadingGame] = useState(null);
  const [launch, setLaunch] = useState(null);

  useEffect(() => {
    let active = true;
    api.get("/casino/games")
      .then(({ data }) => {
        if (active) {
          // Genuine live-dealer provider games are surfaced in the dedicated "Live Dealers"
          // shelf (alongside our native live tables), so exclude them here to avoid
          // duplication. Everything else (JackTop table + crash games) shows in the provider
          // shelf. Classification is data-driven (is_live_dealer), never a "Live" label.
          setGames((data.games || []).filter((game) => game.provider !== "pnr" && game.available
            && !isLiveDealer(game)));
          setLoadFailed(false);
        }
      })
      .catch(() => {
        if (active) { setGames([]); setLoadFailed(true); }
      });
    return () => { active = false; };
  }, []);

  // Apply the lobby search query + category filter here so the provider shelf stays
  // visible (and filtered) while a search is active, instead of being hidden entirely
  // the moment the player types (UIB-10). Match on game name AND provider label.
  const q = String(query || "").trim().toLowerCase();
  const filtered = useMemo(() => {
    let list = games;
    // Slots category: keep only games whose category field marks them as slots. Only
    // applied when a category field actually exists on the data (never fabricate a bucket).
    if (catFilter === "slots") {
      list = list.filter((g) => {
        const cat = String(g.category || g.game_type || g.type || "").toLowerCase();
        return cat.includes("slot");
      });
    }
    if (q) {
      list = list.filter((g) => {
        const label = (PROVIDER_LABEL[g.provider] || g.provider || "").toLowerCase();
        return String(g.name || "").toLowerCase().includes(q) || label.includes(q);
      });
    }
    return list;
  }, [games, q, catFilter]);

  const grouped = useMemo(() => {
    const map = new Map();
    filtered.forEach((game) => {
      const list = map.get(game.provider) || [];
      list.push(game);
      map.set(game.provider, list);
    });
    return Array.from(map.entries());
  }, [filtered]);

  // Featured spotlight (variant="featured"): a curated top-of-lobby row of marquee games, ordered by
  // the backend's featured_rank. Hidden while a search is active (it's a fixed spotlight, not a search
  // result). Featured games also still appear in their normal provider shelf (default variant).
  const featuredList = useMemo(
    () => games.filter((g) => g.featured).sort((a, b) => (a.featured_rank ?? 999) - (b.featured_rank ?? 999)),
    [games]
  );

  // Tell the parent whether this shelf rendered any games so it can decide on a
  // unified "no games match" empty state across every shelf (UIB-10). A catalog LOAD
  // FAILURE is a different condition (handled locally below), so don't report it as an
  // empty search result — that keeps the two states distinct.
  useEffect(() => {
    if (onEmptyChange) onEmptyChange(filtered.length === 0 && !loadFailed);
  }, [filtered.length, loadFailed, onEmptyChange]);

  const launchGame = async (game) => {
    // A launch is already in flight — ignore duplicate taps so we never open two
    // provider sessions / double-submit the POST form (UIB-11b).
    if (loadingGame) return;
    // Launch in the player's selected currency (PCoins GC or PCash SC). External providers
    // don't transact in the bonus wallet, so ask the player to switch first.
    if (currency === "bonus") {
      toast.error("Switch to PCoins or PCash to play provider games.");
      return;
    }
    const wallet = currency === "coins" ? "coins" : "cash";
    const wallets = game.wallets || [];
    if (wallets.length && !wallets.includes(wallet)) {
      toast.error(`${game.name} isn't available for ${wallet === "coins" ? "PCoins" : "PCash"}.`);
      return;
    }
    setLoadingGame(game.id);
    try {
      const { data } = await api.post(
        `/casino/providers/${game.provider}/launch`,
        {
          game_id: game.external_game_id,
          wallet,
          locale: navigator.language || "en",
        }
      );
      setLaunch({ ...data, wallet, gameName: game.name });
    } catch (error) {
      toast.error(apiError(error.response?.data?.detail));
    } finally {
      setLoadingGame(null);
    }
  };

  // The "Featured" variant renders a single curated spotlight shelf (hidden during search); the
  // default variant renders the per-provider shelves. Both share the games fetch, launch + host.
  const shelves = isFeatured
    ? (q || !featuredList.length ? [] : [["__featured__", featuredList]])
    : grouped;
  // Nothing to render when there are genuinely no provider games AND nothing failed —
  // the parent handles the unified empty state. We still render the host (for an open
  // launch) via the fragment below, but the shelves collapse.
  const showShelves = shelves.length > 0;

  return (
    <>
      {/* Catalog LOAD failure (distinct from "no games match a search") — honest,
         not a blank space pretending the catalog is empty. The featured spotlight stays silent on
         failure so it never shows an error where the main provider shelf already does. */}
      {!isFeatured && loadFailed && !games.length && (
        <div className="flex flex-col items-center gap-2 rounded-2xl border border-surface-border bg-surface-3 py-10 text-center" data-testid="provider-catalog-error">
          <Icon name="alert-circle" size={28} className="text-[#EF4444]" />
          <p className="font-display text-base font-black text-white">Couldn&apos;t load provider games</p>
          <p className="text-sm text-[#a1a1aa]">Our own games above are unaffected. Try again in a moment.</p>
        </div>
      )}
      {showShelves && (
      <div className="space-y-8" data-testid={isFeatured ? "featured-provider-games" : "external-provider-games"}>
        {shelves.map(([provider, providerGames]) => (
          <CasinoShelf
            key={provider}
            icon={isFeatured ? "star" : "dice"}
            title={isFeatured ? "Featured" : (PROVIDER_LABEL[provider] || provider)}
            count={providerGames.length}
            subtitle={isFeatured ? "Spotlight games" : "Provider studio"}
            testid={isFeatured ? "shelf-featured" : `provider-shelf-${provider}`}
          >
            {providerGames.map((game) => (
              <CasinoTile
                key={game.id}
                name={game.name}
                img={game.thumbnail_url}
                badge={isFeatured ? "Featured" : (PROVIDER_LABEL[game.provider] || game.provider)}
                provider
                square
                tag={!isFeatured && isLiveDealer(game) ? "LIVE" : null}
                live={false}
                onClick={() => launchGame(game)}
                testid={`provider-game-${game.provider}-${game.external_game_id}`}
              />
            ))}
          </CasinoShelf>
        ))}
      </div>
      )}
      <ProviderGameHost
        launch={launch}
        onClose={async () => {
          const closing = launch;
          setLaunch(null);
          if (closing?.provider && closing?.session_id) {
            try {
              await api.post(
                `/casino/providers/${closing.provider}/sessions/${closing.session_id}/end`
              );
            } catch {
              // The UI should still close. The provider session remains callback-
              // eligible until TTL so late settlement is never orphaned.
            }
          }
          // Refresh the app wallet so any balance the provider changed during the
          // session is reflected the moment the player returns (UIB-11d).
          if (refresh) { try { await refresh(); } catch { /* non-fatal */ } }
        }}
      />
    </>
  );
}
